import { faShare } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Box, DialogTitle, Grid, Paper, Tooltip, Typography, useTheme } from "@mui/material";
import { FC, useCallback, useContext, useMemo, useState } from "react";
import { Permissions, Report, ReportMailScheduleTypes, ReportTemplate } from "../../api/dtos";
import GridActions from "../../components/GridActions";
import { StyledButton, StyledIconButton } from "../../components/StyledButton";
import CollapsibleAppTable from "../../components/tables/CollapsibleAppTable";
import { StyledLink } from "../../components/utility/StyledLink";
import type { AppColumn, AppScalarValue } from "../../components/tables/AppTable.types";
import ReportTemplatesContext from "../../contexts/ReportTemplatesContext";
import { StyledDialog, StyledDialogActions, StyledDialogContent } from "../../forms/inputs/CommonFormElements";
import { getScheduleString } from "../../utility/EnumValueGetters";
import usePostEmailManualTrigger from "../../hooks/actions/usePostEmailManualTrigger";
import usePermissions from "../../hooks/auth/usePermissions";

export const buildEmailOnlyReportsColumns = (
    templates: readonly ReportTemplate[],
    permissions: readonly Permissions[] | undefined,
    onSendEmailClick: (report: Report) => void,
): AppColumn<Report>[] => {
    const templateOptions = templates.flatMap((template) =>
        template.id === null || template.id === undefined
            ? []
            : [
                  {
                      value: template.id,
                      label: template.name ?? String(template.id),
                  },
              ],
    );
    const templateLabel = (value: AppScalarValue) =>
        templates.find((template) => template.id === value)?.name ?? String(value ?? "");
    const columns: AppColumn<Report>[] = [
        {
            id: "name",
            label: "Report Name",
            accessor: (row) => row.name,
            scalarType: "text",
            flex: 1,
            cell: ({ row, formatted }) => <StyledLink to={`/reports/edit/${row.id}`}>{formatted}</StyledLink>,
        },
        {
            id: "templateId",
            label: "Template",
            accessor: (row) => row.templateId,
            scalarType: "lookup",
            flex: 1,
            lookup: {
                options: templateOptions,
                labelFor: (value) => templateLabel(value),
            },
            sortAccessor: (row) => templateLabel(row.templateId),
            sortScalarType: "text",
        },
        {
            id: "scheduleType",
            label: "Schedule",
            accessor: (row) => row.scheduleType,
            scalarType: "lookup",
            width: 200,
            lookup: {
                options: [
                    {
                        value: ReportMailScheduleTypes.Never,
                        label: "Never",
                    },
                    {
                        value: ReportMailScheduleTypes.Daily,
                        label: "Daily",
                    },
                    {
                        value: ReportMailScheduleTypes.Weekly,
                        label: "Weekly",
                    },
                    {
                        value: ReportMailScheduleTypes.Monthly,
                        label: "Monthly",
                    },
                    {
                        value: ReportMailScheduleTypes.Yearly,
                        label: "Yearly",
                    },
                ],
                labelFor: (value) => getScheduleString(scheduleType(value)),
            },
            formatter: (value) => getScheduleString(scheduleType(value)),
            exportAccessor: ({ value }) => getScheduleString(scheduleType(value)),
            sortAccessor: (row) => getScheduleString(row.scheduleType),
            sortScalarType: "text",
        },
        {
            id: "actions",
            label: "",
            headerLabel: "",
            accessor: () => null,
            scalarType: "text",
            kind: "action",
            width: 100,
            filterable: false,
            sortable: false,
            groupable: false,
            exportable: false,
            cell: ({ row }) => (
                <GridActions editUrl={`/reports/edit/${row.id}`} deleteUrl={`/reports/delete/${row.id}`} />
            ),
        },
    ];

    if (permissions?.includes(Permissions.AdminAccess)) {
        columns.unshift({
            id: "sendAction",
            label: "",
            headerLabel: "",
            accessor: () => null,
            scalarType: "text",
            kind: "action",
            width: 50,
            align: "left",
            filterable: false,
            sortable: false,
            groupable: false,
            exportable: false,
            cell: ({ row }) => (
                <Tooltip title="Trigger Email">
                    <StyledIconButton size="small" onClick={() => onSendEmailClick(row)}>
                        <FontAwesomeIcon icon={faShare} />
                    </StyledIconButton>
                </Tooltip>
            ),
        });
    }

    return columns;
};

function scheduleType(value: AppScalarValue): ReportMailScheduleTypes | undefined {
    return typeof value === "number" ? (value as ReportMailScheduleTypes) : undefined;
}

interface IEmailOnlyReportsProps {
    reports: Report[];
    getRowId: (report: Report) => string | number;
}

const EmailOnlyReports: FC<IEmailOnlyReportsProps> = ({ reports, getRowId }) => {
    const theme = useTheme();
    const emailReport = usePostEmailManualTrigger();

    const [reportToEmail, setReportToEmail] = useState<Report | null>(null);
    const [sendEmailDialogOpen, setSendEmailDialogOpen] = useState<boolean>(false);

    const onSendEmailClick = useCallback((report: Report) => {
        setSendEmailDialogOpen(true);
        setReportToEmail(report);
    }, []);

    const onSendMailConfirm = async () => {
        if (reportToEmail) {
            await emailReport(reportToEmail);
        }
        closeDialog();
    };

    const closeDialog = () => {
        setSendEmailDialogOpen(false);
        setReportToEmail(null);
    };

    const exportMetadata = useMemo(
        () => ({
            fileName: `Email Only Reports`,
            exportTitle: `Email Only Reports`,
            includeDateTime: true,
        }),
        [],
    );

    const { data: templates } = useContext(ReportTemplatesContext);
    const { data: permissions } = usePermissions();
    const columns = useMemo(
        () => buildEmailOnlyReportsColumns(templates ?? [], permissions, onSendEmailClick),
        [templates, permissions, onSendEmailClick],
    );

    return (
        <>
            <StyledDialog onClose={() => setSendEmailDialogOpen(false)} open={sendEmailDialogOpen}>
                <DialogTitle
                    sx={{
                        backgroundColor: theme.palette.secondary.light,
                        color: "white",
                    }}
                >
                    Send Email
                </DialogTitle>
                <StyledDialogContent>
                    <Box
                        sx={{
                            mb: 2,
                        }}
                    >
                        <Typography>Are you sure you want to email the following report?</Typography>
                    </Box>
                    <Grid container gap={1}>
                        <Grid
                            size={{
                                xs: 12,
                            }}
                            sx={{
                                display: "flex",
                            }}
                        >
                            <Grid
                                size={{
                                    xs: 6,
                                }}
                            >
                                Name:
                            </Grid>
                            <Grid
                                size={{
                                    xs: 6,
                                }}
                                sx={{
                                    fontWeight: theme.typography.fontWeightBold,
                                    textAlign: "right",
                                }}
                            >
                                {reportToEmail?.name}
                            </Grid>
                        </Grid>
                        <Grid
                            size={{
                                xs: 12,
                            }}
                            sx={{
                                display: "flex",
                            }}
                        >
                            <Grid
                                size={{
                                    xs: 6,
                                }}
                            >
                                To:
                            </Grid>
                            <Grid
                                size={{
                                    xs: 6,
                                }}
                                sx={{
                                    fontWeight: theme.typography.fontWeightBold,
                                    textAlign: "right",
                                }}
                            >
                                {reportToEmail?.mailingList}
                            </Grid>
                        </Grid>
                        <Grid
                            size={{
                                xs: 12,
                            }}
                            sx={{
                                display: "flex",
                            }}
                        >
                            <Grid
                                size={{
                                    xs: 6,
                                }}
                            >
                                CC:
                            </Grid>
                            <Grid
                                size={{
                                    xs: 6,
                                }}
                                sx={{
                                    fontWeight: theme.typography.fontWeightBold,
                                    textAlign: "right",
                                }}
                            >
                                {reportToEmail?.ccList}
                            </Grid>
                        </Grid>
                    </Grid>
                </StyledDialogContent>
                <StyledDialogActions>
                    <StyledButton
                        onClick={async () => {
                            await onSendMailConfirm();
                        }}
                    >
                        Confirm
                    </StyledButton>
                    <StyledButton onClick={closeDialog}>Cancel</StyledButton>
                </StyledDialogActions>
            </StyledDialog>
            <Paper
                sx={{
                    marginBottom: 2,
                }}
            >
                <CollapsibleAppTable
                    density="compact"
                    title={"Email Only Reports"}
                    loading={false}
                    columns={columns}
                    rows={reports}
                    getRowId={getRowId}
                    columnControls={{
                        menu: true,
                    }}
                    filterControls
                    export={exportMetadata}
                />
            </Paper>
        </>
    );
};

export default EmailOnlyReports;
